import { cn } from "@/lib/utils"

type ChoiceCardProps = {
  label: string
  description: string
  /** A glossy product icon from `src/assets/hero/`, the same pack the site's nav rows use. */
  art: string
  onSelect: () => void
  /** Renders the filled dot + primary hairline. Defaults to false. */
  selected?: boolean
  /**
   * `radio` only when the parent wraps these in a `role="radiogroup"`.
   * Otherwise the row keeps button semantics and reports state via
   * `aria-pressed`, so an ungrouped radio never reaches the a11y tree.
   */
  semantics?: "radio" | "button"
}

/**
 * A wizard step is a form, not navigation: the row leads with a radio circle
 * and carries no chevron. The icon sits in the same white hairline well the
 * site's Product dropdown uses (humblytics.com, 2026-09-10), then the label
 * and one line. Selection is a hairline in primary plus the filled dot; the
 * card itself is never painted orange.
 */
export function OnboardingChoiceCard({
  label,
  description,
  art,
  onSelect,
  selected = false,
  semantics = "button",
}: ChoiceCardProps) {
  const isRadio = semantics === "radio"

  return (
    <button
      type="button"
      onClick={onSelect}
      role={isRadio ? "radio" : undefined}
      aria-checked={isRadio ? selected : undefined}
      aria-pressed={isRadio ? undefined : selected}
      className={cn(
        "group flex w-full min-h-6 items-center gap-3.5 rounded-lg border bg-card px-4 py-3 text-left transition-colors",
        "outline-none focus-visible:border-ring focus-visible:ring-[2px] focus-visible:ring-ring/30",
        selected
          ? "border-primary/50 shadow-[0_1px_2px_rgba(0,0,0,0.04)]"
          : "border-border/60 hover:border-border hover:bg-muted/30",
      )}
    >
      <span
        aria-hidden
        className={cn(
          "flex size-[18px] shrink-0 items-center justify-center rounded-full border transition-colors",
          selected ? "border-primary" : "border-border group-hover:border-foreground/40",
        )}
      >
        <span
          className={cn(
            "size-2 rounded-full bg-primary transition-opacity",
            selected ? "opacity-100" : "opacity-0",
          )}
        />
      </span>

      <span
        aria-hidden
        className="flex size-11 shrink-0 items-center justify-center rounded-md border border-border/50 bg-white"
      >
        <img src={art} alt="" draggable={false} className="size-8 object-contain" />
      </span>

      <span className="min-w-0 flex-1">
        <span className="block text-sm font-medium leading-tight text-foreground">{label}</span>
        <span className="mt-0.5 block text-xs leading-snug text-muted-foreground">{description}</span>
      </span>
    </button>
  )
}
